import type {
  Message,
  MessageType,
  ToolCall,
  RichContent,
  ChatAction,
  ChatWidgetProps,
  RetryPolicy,
  SessionConfig,
} from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';
import { parseSSEData } from './sseParser';
import { fetchWithRetry } from '../utils/retryFetch';
import { resolveEndpoint, getNextMessageId } from '../utils/chatHelpers';
import { navigateToUrl } from '../utils/navigation';
import { threadCapabilityHeaders } from '../utils/threadCapability';
import { detectChatAnalyticsIntent } from '../utils/analyticsIntent';

export interface AttachmentPayload {
  name: string;
  type: string;
  size: number;
  dataUrl?: string;
}

export interface MessageResult {
  request: Message;
  response?: Message;
  raw?: unknown;
  aborted?: boolean;
}

export interface StreamingMessageParams {
  // Message content
  text: string;
  attachments?: AttachmentPayload[];
  subject?: string;
  tid?: string;
  sender?: 'user' | 'ai';
  meta?: Record<string, unknown>;
  /** Id for the local echo bubble so the caller can mark it failed later. */
  localMessageId?: string;

  // Endpoint config
  streamEndpoint: string | null | undefined;
  messageCreateEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  threadId: string | null;
  threadBase: string | null;

  // Dependencies
  dispatch: (action: ChatAction) => void;
  mountedRef: { current: boolean };
  abortControllerRef: { current: AbortController | null };
  activeRequestIdRef: { current: string | null };
  withAuthHeaders: (h?: Record<string, string>) => Record<string, string>;
  buildPayload: (args: {
    text: string;
    attachments?: AttachmentPayload[];
    subject?: string;
    tid?: string;
    meta?: Record<string, unknown>;
  }) => Record<string, unknown>;
  buildMessagePayload?: ChatWidgetProps['buildMessagePayload'];
  handleJsonResponse: (
    data: Record<string, unknown>,
    localMsg: Message,
    atts: AttachmentPayload[],
    meta?: Record<string, unknown>
  ) => Promise<MessageResult>;
  onMessageCreate?: (req: Message, res: Message) => void;
  /** Leading `thread` SSE event: capability for a thread the server created implicitly. */
  onThreadIssued?: (issued: { threadId: string; threadToken?: string }) => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  buildAnalyticsEnvelope?: (data?: Record<string, unknown>) => Record<string, unknown>;
  trackOutcome?: (outcome: unknown) => void;
  retryPolicy?: RetryPolicy;
  /** Selects the storage the thread capability token lives in (`storage: 'session'`). */
  sessionConfig?: SessionConfig;

  // Feature flags
  enableToolVisualization: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  enableRichMessages: boolean;
}

type FrameHandle =
  | { kind: 'raf'; id: number }
  | { kind: 'timeout'; id: ReturnType<typeof setTimeout> };

/** Fallback cadence when requestAnimationFrame is unavailable or the tab is hidden. */
const FALLBACK_FRAME_MS = 16;

/**
 * Schedule `cb` for the next animation frame. Falls back to a 16ms timer when rAF is
 * missing (SSR/tests) or the document is hidden (background tabs never run rAF).
 */
function scheduleFrame(cb: () => void): FrameHandle {
  const hidden = typeof document !== 'undefined' && document.hidden === true;
  if (!hidden && typeof requestAnimationFrame === 'function') {
    return { kind: 'raf', id: requestAnimationFrame(() => cb()) };
  }
  return { kind: 'timeout', id: setTimeout(cb, FALLBACK_FRAME_MS) };
}

function cancelFrame(handle: FrameHandle): void {
  if (handle.kind === 'raf') {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle.id);
  } else {
    clearTimeout(handle.id);
  }
}

export async function createStreamingMessageFn(
  params: StreamingMessageParams
): Promise<MessageResult> {
  const {
    text,
    attachments: atts = [],
    subject: subj,
    tid,
    sender = 'user',
    meta,
    localMessageId,
    streamEndpoint,
    messageCreateEndpoint,
    messageEndpoint,
    apiBaseUrl,
    threadId,
    threadBase,
    dispatch,
    mountedRef,
    abortControllerRef,
    activeRequestIdRef,
    withAuthHeaders,
    buildPayload,
    buildMessagePayload,
    handleJsonResponse,
    onMessageCreate,
    onThreadIssued,
    trackAnalytics,
    buildAnalyticsEnvelope,
    trackOutcome,
    retryPolicy,
    sessionConfig,
    enableToolVisualization,
    enableSourceCitations,
    enableThinking,
    enableRichMessages,
  } = params;

  const endpoint = resolveEndpoint({
    primary: streamEndpoint,
    messageCreate: messageCreateEndpoint,
    message: messageEndpoint,
    threadBase,
    tid: tid || threadId,
    apiBaseUrl,
  });
  if (!endpoint) throw new Error('No message endpoint configured');

  const requestId = `${getNextMessageId()}-ai`;
  activeRequestIdRef.current = requestId;
  abortControllerRef.current?.abort();
  dispatch({ type: 'CLEAR_STREAMING' });
  const isActiveRequest = () => mountedRef.current && activeRequestIdRef.current === requestId;

  const now = new Date();
  const localMsg: Message = {
    id: localMessageId ?? getNextMessageId(),
    sender,
    text,
    timestamp: now.toISOString(),
    status: 'sent',
  };
  if (!(meta && typeof meta === 'object' && meta.silent === true)) {
    dispatch({ type: 'ADD_MESSAGE', message: localMsg });
  }

  const payload = {
    ...buildPayload({ text, attachments: atts, subject: subj, tid, meta }),
    requestId,
    stream: true,
  };

  const bodyPayload =
    typeof buildMessagePayload === 'function' ? buildMessagePayload(payload, localMsg) : payload;

  // Accumulators live outside the try so a user-initiated Stop can keep what
  // already streamed in.
  let accumulatedText = '';
  const toolCalls: ToolCall[] = [];
  const sources: { id: string; title: string; url: string; snippet?: string }[] = [];
  let thinking = '';
  let richContent: RichContent | undefined = undefined;
  let richType: MessageType | null = null;
  // Stored AI message id from the `message_id` event (server contract), if sent.
  let serverMessageId: string | undefined;
  const streamedMessageId = requestId;

  // Text deltas are coalesced: `accumulatedText` is always exact, but the UPDATE_STREAMING
  // dispatch (which re-renders the whole widget) happens at most once per animation frame.
  // Any non-text event, completion, abort or error flushes pending text first so ordering
  // is preserved.
  let pendingTextUpdate = false;
  let scheduledFrame: FrameHandle | null = null;
  const cancelScheduledFrame = () => {
    if (scheduledFrame) {
      cancelFrame(scheduledFrame);
      scheduledFrame = null;
    }
  };
  const flushStreamingText = () => {
    cancelScheduledFrame();
    if (!pendingTextUpdate) return;
    pendingTextUpdate = false;
    if (isActiveRequest()) {
      dispatch({ type: 'UPDATE_STREAMING', text: accumulatedText, requestId });
    }
  };
  const queueStreamingText = () => {
    pendingTextUpdate = true;
    if (scheduledFrame) return;
    scheduledFrame = scheduleFrame(() => {
      scheduledFrame = null;
      flushStreamingText();
    });
  };

  try {
    // Each request owns its controller and can only update its own turn.
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const res = await fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        // Keep a conversation on the same backend replica when the widget API
        // is hosted on a different origin and ingress uses cookie affinity.
        credentials: 'include',
        headers: {
          ...withAuthHeaders(),
          ...threadCapabilityHeaders(tid || threadId, sessionConfig),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(bodyPayload),
        signal: controller.signal,
        timeoutMs: 60_000,
      },
      retryPolicy,
      {
        onRetry: metadata => {
          trackAnalytics(
            'message_retry',
            buildAnalyticsEnvelope
              ? buildAnalyticsEnvelope({
                  attempt: metadata.attempt,
                  reason: metadata.reason,
                  status: metadata.status,
                  streaming: true,
                })
              : {
                  attempt: metadata.attempt,
                  reason: metadata.reason,
                  status: metadata.status,
                  streaming: true,
                }
          );
        },
      }
    );

    if (!res.ok) {
      let serverError = '';
      try {
        const json = (await res.json()) as Record<string, unknown>;
        if (typeof json?.error === 'string') {
          serverError = json.error;
        } else if (typeof json?.message === 'string') {
          serverError = json.message;
        }
      } catch {
        // Keep generic status code message if body is not JSON.
      }
      throw new Error(`Streaming failed: ${res.status}${serverError ? ` (${serverError})` : ''}`);
    }

    const contentType =
      typeof res.headers?.get === 'function' ? res.headers.get('content-type') || '' : '';
    const normalizedType = contentType.toLowerCase();
    const isJsonResponse =
      normalizedType.includes('application/json') ||
      normalizedType.includes('application/problem+json');

    if (isJsonResponse) {
      const data = await res.json();
      if (!isActiveRequest()) return { request: localMsg, aborted: true };
      return await handleJsonResponse(data as Record<string, unknown>, localMsg, atts, meta);
    }

    if (!res.body) {
      try {
        const data = await res.json();
        if (!isActiveRequest()) return { request: localMsg, aborted: true };
        return await handleJsonResponse(data as Record<string, unknown>, localMsg, atts, meta);
      } catch {
        throw new Error('Response body is not readable');
      }
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const requestStartedAt = Date.now();

    let buffer = '';
    let dataLines: string[] = [];
    let streamDone = false;

    const flushDataLines = () => {
      if (dataLines.length === 0) return;
      const data = dataLines.join('\n').trim();
      dataLines = [];
      if (data === '[DONE]') {
        streamDone = true;
        return;
      }
      const event = parseSSEData(data, {
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        enableRichMessages,
      });
      if (!event) return;
      if (event.type !== 'text_delta' && event.type !== 'text_replace') {
        // Keep ordering: the UI must see the text that preceded this event first.
        flushStreamingText();
      }
      switch (event.type) {
        case 'thread':
          onThreadIssued?.({ threadId: event.threadId, threadToken: event.threadToken });
          break;
        case 'message_id':
          serverMessageId = event.messageId;
          break;
        case 'text_delta':
          accumulatedText += event.text;
          queueStreamingText();
          break;
        case 'text_replace':
          // The server's final text differs from what streamed (a later rewrite changed an
          // earlier sentence): show the final text.
          accumulatedText = event.text;
          queueStreamingText();
          break;
        case 'outcome':
          trackOutcome?.(event.outcome);
          break;
        case 'tool_call':
          toolCalls.push(event.toolCall);
          trackAnalytics(
            'tool_call',
            buildAnalyticsEnvelope
              ? buildAnalyticsEnvelope({
                  threadId: tid || threadId || undefined,
                  toolName: event.toolCall.name,
                  toolCallId: event.toolCall.id,
                  status: event.toolCall.status || 'started',
                  streamed: true,
                })
              : {
                  toolName: event.toolCall.name,
                  toolCallId: event.toolCall.id,
                  status: event.toolCall.status || 'started',
                  streamed: true,
                }
          );
          if (isActiveRequest()) dispatch({ type: 'ADD_TOOL_CALL', toolCall: event.toolCall });
          break;
        case 'tool_result': {
          const idx = toolCalls.findIndex(t => t.id === event.id);
          if (idx >= 0) {
            toolCalls[idx].result = event.result;
            toolCalls[idx].status = 'complete';
            if (isActiveRequest())
              dispatch({ type: 'UPDATE_TOOL_CALLS', toolCalls: [...toolCalls] });
          }
          break;
        }
        case 'citation':
          sources.push(event.source);
          break;
        case 'thinking':
          thinking += event.text;
          break;
        case 'rich_content':
          richContent = event.content as RichContent;
          richType = event.messageType as MessageType;
          break;
        case 'escalated':
          if (isActiveRequest()) dispatch({ type: 'SET_ESCALATED', isEscalated: true });
          trackAnalytics(
            'handoff_request',
            buildAnalyticsEnvelope
              ? buildAnalyticsEnvelope({
                  threadId: tid || threadId || undefined,
                  streamed: true,
                  source: 'stream',
                })
              : { streamed: true, source: 'stream' }
          );
          break;
        case 'redirect':
          navigateToUrl(event.url, event.target);
          break;
      }
    };

    const processLine = (line: string) => {
      if (streamDone) return;
      if (line === '') {
        flushDataLines();
        return;
      }
      if (line.startsWith('data:')) {
        let linePayload = line.slice(5);
        if (linePayload.startsWith(' ')) linePayload = linePayload.slice(1);
        dataLines.push(linePayload);
      }
    };

    const processBuffer = () => {
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        processLine(line);
        if (streamDone) break;
      }
    };

    if (!normalizedType.includes('text/event-stream')) {
      const MAX_JSON_FALLBACK_BYTES = 64_000;
      const safeTrimmed = (value: string) => value.replace(/^\uFEFF/, '').trim();
      const parseLikelyJson = (value: string) => {
        const cleaned = safeTrimmed(value);
        if (!cleaned) return null;
        try {
          return JSON.parse(cleaned);
        } catch {
          return null;
        }
      };

      const first = await reader.read();
      if (!first.done && first.value) {
        const firstText = decoder.decode(first.value, { stream: true });
        const trimmed = firstText.trimStart();
        const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
        const looksLikeSse = /(^|[\r\n])data:|(^|[\r\n])event:|(^|[\r\n]):/.test(firstText);
        if (looksLikeJson && !looksLikeSse) {
          let jsonText = firstText;
          let reachedLimit = false;
          while (true) {
            const next = await reader.read();
            if (next.done) break;
            jsonText += decoder.decode(next.value, { stream: true });
            if (jsonText.length > MAX_JSON_FALLBACK_BYTES) {
              reachedLimit = true;
              break;
            }
          }
          const maybeParsed = !reachedLimit ? parseLikelyJson(jsonText) : null;
          if (maybeParsed) {
            if (!isActiveRequest()) return { request: localMsg, aborted: true };
            return await handleJsonResponse(
              maybeParsed as Record<string, unknown>,
              localMsg,
              atts,
              meta
            );
          }
          buffer += jsonText;
        } else {
          buffer += firstText;
        }
      }
    }

    if (!isActiveRequest())
      return {
        request: localMsg,
        response: {
          id: streamedMessageId,
          sender: 'ai',
          text: '',
          timestamp: '',
          status: 'delivered',
        } as Message,
      };
    dispatch({
      type: 'SET_STREAMING',
      message: {
        id: streamedMessageId,
        sender: 'ai',
        text: '',
        timestamp: new Date().toISOString(),
        status: 'streaming',
        type: MESSAGE_TYPES.TEXT,
      },
    });

    if (buffer) processBuffer();

    const STREAM_IDLE_TIMEOUT = 60_000;
    const IDLE = Symbol('stream-idle');
    while (reader && isActiveRequest() && !streamDone) {
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      const idlePromise = new Promise<typeof IDLE>(resolve => {
        idleTimer = setTimeout(() => resolve(IDLE), STREAM_IDLE_TIMEOUT);
      });
      let readResult: ReadableStreamReadResult<Uint8Array> | typeof IDLE;
      try {
        readResult = await Promise.race([reader.read(), idlePromise]);
      } finally {
        // One timer per chunk: always clear it once the read settles so timers never pile up.
        if (idleTimer) clearTimeout(idleTimer);
      }
      if (readResult === IDLE) {
        // The server stopped sending without closing the stream. Tear the connection down
        // and surface it as a failure rather than silently treating it as a completed reply.
        try {
          await reader.cancel();
        } catch {
          // Reader may already be closed/errored; nothing else to release.
        }
        controller.abort();
        throw new Error(
          `Streaming timed out: no data received for ${Math.round(STREAM_IDLE_TIMEOUT / 1000)}s`
        );
      }
      if (readResult.done || !isActiveRequest()) break;

      buffer += decoder.decode(readResult.value, { stream: true });
      processBuffer();
    }

    if (!streamDone) {
      if (buffer) processLine(buffer);
      flushDataLines();
    }

    flushStreamingText();

    const finalMessage: Message = {
      id: streamedMessageId,
      ...(serverMessageId ? { serverId: serverMessageId } : {}),
      sender: 'ai',
      text: accumulatedText,
      timestamp: new Date().toISOString(),
      status: 'delivered',
      type: richType ?? MESSAGE_TYPES.TEXT,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      sources: sources.length > 0 ? sources : undefined,
      thinking: thinking || undefined,
      richContent: richContent || undefined,
    };

    if (!isActiveRequest()) return { request: localMsg, aborted: true };

    dispatch({ type: 'CLEAR_STREAMING', requestId });
    dispatch({ type: 'ADD_MESSAGE', message: finalMessage });
    activeRequestIdRef.current = null;
    if (
      finalMessage.type === MESSAGE_TYPES.PRODUCT_CARDS &&
      finalMessage.richContent?.products?.length
    ) {
      trackAnalytics('product_recommendations', {
        ...(buildAnalyticsEnvelope ? buildAnalyticsEnvelope() : {}),
        count: finalMessage.richContent.products.length,
        ids: finalMessage.richContent.products.map(p => p?.id).filter(Boolean),
      });
    }
    onMessageCreate?.(localMsg, finalMessage);
    trackAnalytics(
      'message_sent',
      buildAnalyticsEnvelope
        ? buildAnalyticsEnvelope({
            threadId: tid || threadId || undefined,
            messageId: localMsg.id,
            messageType: localMsg.type || MESSAGE_TYPES.TEXT,
            sender: localMsg.sender,
            characterCount: localMsg.text.length,
            intent: detectChatAnalyticsIntent(localMsg.text),
            hasAttachments: atts?.length > 0,
            attachmentCount: atts?.length || 0,
            streamed: true,
            hadToolCalls: toolCalls.length > 0,
            responseTime: Date.now() - requestStartedAt,
            silent: meta?.silent === true,
          })
        : {
            hasAttachments: atts?.length > 0,
            streamed: true,
            hadToolCalls: toolCalls.length > 0,
          }
    );

    return { request: localMsg, response: finalMessage };
  } catch (err) {
    // Show whatever text was pending before the stream is torn down (no-op when inactive).
    flushStreamingText();
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Still the active request and still mounted means the visitor pressed Stop.
      // A supersede (newer send) or unmount clears the active id first, and the idle
      // timeout throws a plain Error, so neither reaches this branch as "user stop".
      const stoppedByUser = isActiveRequest();
      if (activeRequestIdRef.current === requestId) {
        dispatch({ type: 'CLEAR_STREAMING', requestId });
        activeRequestIdRef.current = null;
      }
      if (stoppedByUser && accumulatedText) {
        const partialMessage: Message = {
          id: streamedMessageId,
          ...(serverMessageId ? { serverId: serverMessageId } : {}),
          sender: 'ai',
          text: accumulatedText,
          timestamp: new Date().toISOString(),
          status: 'delivered',
          type: richType ?? MESSAGE_TYPES.TEXT,
          toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
          sources: sources.length > 0 ? sources : undefined,
          thinking: thinking || undefined,
          richContent: richContent || undefined,
          interrupted: true,
        };
        dispatch({ type: 'ADD_MESSAGE', message: partialMessage });
        return { request: localMsg, response: partialMessage, aborted: true };
      }
      return {
        request: localMsg,
        aborted: true,
      };
    }
    // The connection dropped or went idle after part of the reply arrived: keep what the
    // shopper already saw as an interrupted reply instead of replacing it with a send failure.
    const keepPartial = isActiveRequest() && (accumulatedText.trim() || richContent);
    if (activeRequestIdRef.current === requestId) {
      dispatch({ type: 'CLEAR_STREAMING', requestId });
      activeRequestIdRef.current = null;
    }
    if (keepPartial) {
      const partialMessage: Message = {
        id: streamedMessageId,
        ...(serverMessageId ? { serverId: serverMessageId } : {}),
        sender: 'ai',
        text: accumulatedText,
        timestamp: new Date().toISOString(),
        status: 'delivered',
        type: richType ?? MESSAGE_TYPES.TEXT,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        sources: sources.length > 0 ? sources : undefined,
        thinking: thinking || undefined,
        richContent: richContent || undefined,
        interrupted: true,
      };
      dispatch({ type: 'ADD_MESSAGE', message: partialMessage });
      return { request: localMsg, response: partialMessage };
    }
    throw err;
  } finally {
    // Never leave a frame scheduled past the request's lifetime (completion/abort/unmount).
    cancelScheduledFrame();
  }
}
